/*
  cm / inches toggle for size charts.

  Tables are authored in metric only (the metaobjects hold cm, footwear holds mm),
  so inches are derived here rather than stored twice. The shopper's choice is
  remembered across pages and tables.

  Which cells convert is decided by the column heading: only columns whose header
  declares a unit are touched, e.g. "Bust (cm)" or "Foot Length (mm)". That leaves
  the Size, UK, EU and US ladders alone, which must never be converted.
*/
(() => {
  const STORE_KEY = 'size-chart-units';
  const PER_UNIT = { cm: 2.54, mm: 25.4 };

  /* Matches a unit in a heading: "Bust (cm)", "Shoulder (seam, cm)", "Foot Length (mm)". */
  const UNIT_RE = /\((?:[^)]*,\s*)?(cm|mm)\)\s*$/i;

  function headerUnit(text) {
    const m = (text || '').match(UNIT_RE);
    return m ? m[1].toLowerCase() : null;
  }

  /* Values are plain numbers or ranges ("76-78", "34–36"). Unsigned on purpose:
     allowing a leading minus would swallow the hyphen in a range as a sign, which
     happens to round-trip correctly but only by accident. Matching digits only
     leaves the separator alone, whether it's a hyphen or an en dash. */
  const NUMBERS_RE = /\d+(?:\.\d+)?/g;

  /* Below this, a column needs a decimal to stay meaningful: a 14.5cm shoulder
     seam is 5.7in and whole inches would flatten it to 6. At or above it, whole
     inches read better and match how these tables were written before they went
     metric -- 86-91cm was published as 34-36, not 33.9-35.8. */
  const WHOLE_INCH_FLOOR = 20;

  function toInches(text, unit, decimals) {
    const divisor = PER_UNIT[unit];
    const converted = text.replace(NUMBERS_RE, (n) => {
      const v = parseFloat(n) / divisor;
      return String(decimals ? Math.round(v * 10) / 10 : Math.round(v));
    });
    /* Narrow cm bands can round to the same whole inch at both ends (80-82cm is
       32-32), so show one figure rather than a range that says nothing. */
    const range = converted.match(/^(\d+(?:\.\d+)?)([-–])(\d+(?:\.\d+)?)$/);
    if (range && range[1] === range[3]) return range[1];
    return converted;
  }

  /* Whether a column keeps a decimal is decided once for the whole column, from
     its largest value, so a single column never mixes 9.9 with 12. */
  function needsDecimals(values, unit) {
    const divisor = PER_UNIT[unit];
    let max = 0;
    values.forEach((text) => {
      (text.match(NUMBERS_RE) || []).forEach((n) => {
        max = Math.max(max, parseFloat(n) / divisor);
      });
    });
    return max < WHOLE_INCH_FLOOR;
  }

  class SizeChartUnits extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.table = this.querySelector('table');
      if (!this.table) return;

      this.units = this.columnUnits();
      if (!this.units.some(Boolean)) return;   // nothing convertible, so no toggle
      this.cacheOriginals();
      this.decimals = this.columnDecimals();
      this.renderToggle();
      this.apply(this.preferred());
    }

    columnUnits() {
      return [...this.table.querySelectorAll('thead th')].map((th) => headerUnit(th.textContent));
    }

    columnDecimals() {
      const rows = [...this.table.querySelectorAll('tbody tr')];
      return this.units.map((unit, i) => {
        if (!unit) return false;
        const column = rows.map((tr) => tr.children[i]?.dataset.metric ?? '');
        return needsDecimals(column, unit);
      });
    }

    cacheOriginals() {
      this.table.querySelectorAll('thead th, tbody tr > *').forEach((cell) => {
        cell.dataset.metric = cell.textContent.trim();
      });
    }

    preferred() {
      try {
        const saved = localStorage.getItem(STORE_KEY);
        if (saved === 'cm' || saved === 'in') return saved;
      } catch (e) {
        /* Private browsing: fall back to the store default. */
      }
      return this.dataset.default === 'in' ? 'in' : 'cm';
    }

    renderToggle() {
      const labels = {
        cm: this.dataset.labelCm || 'cm',
        in: this.dataset.labelIn || 'inches',
      };
      this.toggle = document.createElement('div');
      this.toggle.className = 'size-chart__units';
      this.toggle.setAttribute('role', 'group');
      this.toggle.setAttribute('aria-label', this.dataset.labelGroup || 'Measurement units');
      ['cm', 'in'].forEach((unit) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'size-chart__unit';
        button.dataset.unit = unit;
        button.textContent = labels[unit];
        button.addEventListener('click', () => {
          this.apply(unit);
          try {
            localStorage.setItem(STORE_KEY, unit);
          } catch (e) {
            /* Not worth failing over. */
          }
          /* Keep every other table on the page in step. */
          document.querySelectorAll('size-chart-units').forEach((el) => {
            if (el !== this) el.apply?.(unit);
          });
        });
        this.toggle.appendChild(button);
      });
      this.insertBefore(this.toggle, this.table);
    }

    apply(unit) {
      const rows = this.table.querySelectorAll('tbody tr');
      const heads = this.table.querySelectorAll('thead th');
      heads.forEach((th, i) => {
        const metric = th.dataset.metric ?? th.textContent;
        th.textContent = unit === 'in' && this.units[i]
          ? metric.replace(UNIT_RE, (m) => m.replace(/(cm|mm)/i, 'in'))
          : metric;
      });
      rows.forEach((tr) => {
        [...tr.children].forEach((cell, i) => {
          const metric = cell.dataset.metric ?? cell.textContent;
          const columnUnit = this.units[i];
          cell.textContent = unit === 'in' && columnUnit
            ? toInches(metric, columnUnit, this.decimals[i])
            : metric;
        });
      });
      this.toggle?.querySelectorAll('.size-chart__unit').forEach((b) => {
        const on = b.dataset.unit === unit;
        b.classList.toggle('size-chart__unit--active', on);
        b.setAttribute('aria-pressed', String(on));
      });
    }
  }

  if (!customElements.get('size-chart-units')) {
    customElements.define('size-chart-units', SizeChartUnits);
  }
})();
